import type { SiteSeo, WorkspacePage } from "../api/contract";

type ContentArea = "blog" | "shop";
type AreaNames = Partial<Record<ContentArea, { name: string; base: string } | null>>;

export interface OutlinePageGroup {
  page: WorkspacePage;
  templates: WorkspacePage[];
  area: ContentArea | null;
}

/** Keep each list page and its templates together, even if the route order interleaves them. */
export function outlinePageGroups(pages: WorkspacePage[]): OutlinePageGroup[] {
  const parents = new Set(pages.filter(page => !page.template).map(page => page.path));
  return pages.filter(page => !page.template || !parents.has(page.template.parent)).map(page => {
    const templates = pages.filter(template => template.template?.parent === page.path);
    const kind = templates[0]?.template?.kind;
    return { page, templates, area: kind === "post" || kind === "tag" ? "blog" : kind === "product" || kind === "category" ? "shop" : null };
  });
}

/** Area names belong to their settings; stored page labels avoid waiting for the preview bridge. */
export function outlinePageTitles(pages: WorkspacePage[], { areas = {}, stored = [], preview = {} }: {
  areas?: AreaNames;
  stored?: Pick<SiteSeo["pages"][number], "path" | "label" | "title">[];
  preview?: Record<string, string>;
}): Record<string, string> {
  const labels = new Map(stored.map(page => [page.path, page]));
  const areaByPath = new Map(outlinePageGroups(pages).map(group => [group.page.path, group.area]));
  return Object.fromEntries(pages.map(page => {
    if (page.template || page.special) return [page.path, page.title];
    const area = areas.blog?.base === page.path ? "blog" : areas.shop?.base === page.path ? "shop" : areaByPath.get(page.path);
    if (area) return [page.path, areas[area]?.name.trim() || (area === "blog" ? "Blog" : "Shop")];
    const saved = labels.get(page.path);
    return [page.path, saved?.label?.trim() || saved?.title?.trim() || preview[page.path]?.trim() || page.title];
  }));
}
